/*
 * Design: Gradient Noir – Premium portfolio showcase page
 * Enhanced animations, glassmorphism, glow effects, smooth modal
 * Brand accent #66CDAA
 */
import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { useApiData } from "@/hooks/useApiData";
import type { Project } from "@shared/types";
import { ArrowLeft, Layers, Eye } from "lucide-react";

/* ─── Project Card (Enhanced) ─── */
function ProjectCard({
  project,
  index,
}: {
  project: Project;
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <Link
      href={`/projects/${project.slug}`}
      ref={ref as any}
      className={`group relative block transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Card wrapper with enhanced hover: elevation + glow + border */}
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-sm transition-all duration-500 ease-out group-hover:border-[#66CDAA]/30 group-hover:bg-white/[0.05] group-hover:shadow-[0_8px_60px_rgba(102,205,170,0.12),0_4px_20px_rgba(0,0,0,0.3)] group-hover:-translate-y-2">
        {/* Image placeholder – 16:9 wide rectangle with zoom on hover */}
        <div className="relative aspect-[2/1] bg-gradient-to-br from-[#0B1120] via-[#0F172A] to-[#131D30] overflow-hidden">
          {project.image ? (
            <img
              src={project.image}
              alt={project.name}
              className="w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-110"
            />
          ) : (
            <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-105">
              {/* Animated grid pattern */}
              <div className="absolute inset-0 opacity-[0.04] group-hover:opacity-[0.07] transition-opacity duration-700"
                style={{
                  backgroundImage: `linear-gradient(rgba(102,205,170,1) 1px, transparent 1px), linear-gradient(90deg, rgba(102,205,170,1) 1px, transparent 1px)`,
                  backgroundSize: "40px 40px",
                }}
              />
              {/* Radial glow on hover */}
              <div className="absolute inset-0 bg-radial-[circle_at_center] from-[#66CDAA]/[0.06] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              {/* Center icon */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3">
                <div className="w-16 h-16 rounded-2xl border border-[#66CDAA]/15 bg-[#66CDAA]/[0.04] flex items-center justify-center transition-all duration-500 group-hover:border-[#66CDAA]/40 group-hover:bg-[#66CDAA]/10 group-hover:scale-110 group-hover:rotate-3">
                  <Layers className="w-7 h-7 text-[#66CDAA]/30 group-hover:text-[#66CDAA]/70 transition-all duration-500" />
                </div>
                <span className="text-xs text-white/15 group-hover:text-white/40 transition-all duration-500">תצוגה מקדימה</span>
              </div>
              {/* Corner accents with animation */}
              <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#66CDAA]/[0.07] rounded-tr-lg group-hover:border-[#66CDAA]/30 group-hover:w-10 group-hover:h-10 transition-all duration-500" />
              <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#66CDAA]/[0.07] rounded-bl-lg group-hover:border-[#66CDAA]/30 group-hover:w-10 group-hover:h-10 transition-all duration-500" />
            </div>
          )}
          {/* Gradient overlay – always subtle, stronger on hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120]/90 via-[#0B1120]/20 to-transparent opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
          {/* View CTA on hover */}
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[2] flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#66CDAA] text-[#0B1120] text-sm font-bold opacity-0 translate-y-6 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out shadow-[0_4px_20px_rgba(102,205,170,0.3)]">
            <Eye className="w-4 h-4" />
            <span>צפייה בפרויקט</span>
          </div>
        </div>

        {/* Card info */}
        <div className="p-5 border-t border-white/[0.04]">
          <h3 className="text-lg font-bold font-[Rubik] text-white group-hover:text-[#66CDAA] transition-colors duration-400">
            {project.name}
          </h3>
        </div>

        {/* Glow line at bottom on hover */}
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#66CDAA]/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </Link>
  );
}
/* ─── Projects Page ─── */
export default function Projects() {
  const { data: projects } = useApiData<Project[]>("/projects");
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    requestAnimationFrame(() => setHeroVisible(true));
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* ─── Hero Section ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#66CDAA]/[0.04] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#66CDAA]/[0.03] rounded-full blur-[100px]" />
        </div>

        <div className="container relative">
          <div
            className={`text-center transition-all duration-1000 ${
              heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            {/* Title – starts directly, no subtitle badge */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black font-[Rubik] text-white mb-6 leading-[1.1]">
              פרויקטים{" "}
              <span className="relative inline-block">
                <span className="text-transparent bg-clip-text bg-gradient-to-l from-[#66CDAA] to-[#3DD5B0]">
                  שמדברים
                </span>
                <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-l from-[#66CDAA]/0 via-[#66CDAA]/60 to-[#66CDAA]/0 rounded-full" />
              </span>{" "}
              בעד עצמם
            </h1>

            <p className="text-base md:text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
              הפרויקטים שלפניכם מציגים חלק מהאתרים שבנינו עבור לקוחותינו לאורך השנים.
              <br />
              כל אתר נבנה מתוך הבנה עמוקה של העסק, הקהל והיעדים – במטרה ליצור נכס דיגיטלי שמשרת את העסק לאורך זמן.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Projects Grid – no stats bar ─── */}
      <section className="relative pb-24 md:pb-32">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {(projects || []).map((project, i) => (
              <ProjectCard
                key={project.slug}
                project={project}
                index={i}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#66CDAA]/[0.03] to-transparent" />
        <div className="container relative text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-[Rubik] text-white mb-4">
            רוצים פרויקט כזה?
          </h2>
          <p className="text-white/50 mb-8 max-w-lg mx-auto">
            בואו נדבר על הפרויקט הבא שלכם ונהפוך את החזון שלכם למציאות דיגיטלית.
          </p>
          <a
            href="/contact"
            className="glow-btn inline-flex items-center gap-2 text-base px-8 py-3.5"
          >
            <span>דברו איתנו</span>
            <ArrowLeft className="w-5 h-5" />
          </a>
        </div>
      </section>

    </div>
  );
}
